import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { myToaster } from '@interstellar-component'
import { useApp } from '@src/AppContext'
import { DashboardService } from './service'

const DashboardContext = createContext()

const STORAGE_KEY = 'everest:dashboard:company_id'
const KOL_KEYS = ['all', 'kol_1', 'kol_2', 'kol_3', 'kol_4', 'kol_5']
const EMPTY_METRICS = KOL_KEYS.map((key) => ({
  key,
  label: key === 'all' ? 'All KOL' : key.replace('_', ' ').toUpperCase(),
  value: 0,
  active: key === 'all',
}))
const EMPTY_SERIES = [1, 2, 3, 4, 5].map((k) => ({
  name: `KOL ${k}`,
  data: new Array(12).fill(0),
}))

// Translate the active metric card into the `kol` query param the
// /v1/dashboard/employees endpoint expects. The "all" card returns null
// (no filter).
function activeKey(metrics) {
  return metrics.find((m) => m.active)?.key ?? 'all'
}
function kolParamFromActiveKey(key) {
  if (!key || key === 'all') return null
  const m = /^kol_(\d)$/.exec(key)
  return m ? parseInt(m[1], 10) : null
}

function DashboardProvider({ children }) {
  const { user } = useApp()

  // ─── Company state ────────────────────────────────────────────────────────
  const companies = useMemo(() => user?.companies ?? [], [user])
  const [companyId, setCompanyIdState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null
    } catch {
      return null
    }
  })

  const setCompanyId = useCallback((id) => {
    setCompanyIdState(id)
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id)
      else localStorage.removeItem(STORAGE_KEY)
    } catch {
      // ignore quota / privacy mode errors
    }
  }, [])

  // Pin to a sensible default once session resolves. Prefer the persisted
  // selection if it's still in the user's company list; otherwise the first.
  // Persist via setCompanyId so a stale localStorage id (e.g. another tenant's
  // company from a previous login) is overwritten, not just shadowed in state.
  useEffect(() => {
    if (!companies.length) return
    const validPersisted = companyId && companies.some((c) => c.id === companyId)
    if (!validPersisted) {
      setCompanyId(companies[0].id)
    }
  }, [companies, companyId, setCompanyId])

  const activeCompany = useMemo(
    () => companies.find((c) => c.id === companyId) || null,
    [companies, companyId],
  )

  // Only ever fetch for a company the user actually belongs to. Until companyId
  // is validated against the user's companies (the pin-default effect above),
  // this is null and no request fires — so a stale/cross-tenant id never reaches
  // the API and triggers a "Company not accessible" 403.
  const effectiveCompanyId = activeCompany?.id ?? null

  // ─── Header / metrics / chart state ───────────────────────────────────────
  const [overview, setOverview] = useState(null)
  const [metrics, setMetrics] = useState(EMPTY_METRICS)
  const [employeeSeries, setEmployeeSeries] = useState(EMPTY_SERIES)
  const [candidateSeries, setCandidateSeries] = useState(EMPTY_SERIES)
  const [chartLabels, setChartLabels] = useState([
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ])

  // ─── Table state ──────────────────────────────────────────────────────────
  const [employees, setEmployees] = useState([])
  const [employeesMeta, setEmployeesMeta] = useState({
    current_page: 1,
    next_page: null,
    prev_page: null,
    total_page: 1,
    total: 0,
  })
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState(null)
  const [sortOrder, setSortOrder] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // ─── Fetchers ─────────────────────────────────────────────────────────────
  const fetchOverview = useCallback(async (cid) => {
    if (!cid) return
    try {
      const res = await DashboardService.getOverview({ company_id: cid })
      setOverview(res?.data?.company || null)
    } catch (e) {
      myToaster(e)
    }
  }, [])

  const fetchMetrics = useCallback(async (cid, currentActiveKey = 'all') => {
    if (!cid) return
    try {
      const res = await DashboardService.getMetrics({ company_id: cid })
      const next = (res?.data || []).map((m) => ({
        ...m,
        active: m.key === currentActiveKey,
      }))
      setMetrics(next.length ? next : EMPTY_METRICS)
    } catch (e) {
      myToaster(e)
    }
  }, [])

  const fetchChart = useCallback(async (cid) => {
    if (!cid) return
    try {
      const res = await DashboardService.getChart({ company_id: cid })
      const payload = res?.data || {}
      setChartLabels(payload.labels || chartLabels)
      setEmployeeSeries(payload.employee?.length ? payload.employee : EMPTY_SERIES)
      setCandidateSeries(payload.candidate?.length ? payload.candidate : EMPTY_SERIES)
    } catch (e) {
      myToaster(e)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchEmployees = useCallback(
    async (cid, { search, kol, sort, order, pageArg }) => {
      if (!cid) return
      try {
        setIsLoading(true)
        const params = {
          company_id: cid,
          page: pageArg ?? 1,
          limit,
        }
        if (search) params.search = search
        if (kol != null) params.kol = kol
        if (sort && order) {
          params.sort = sort
          params.order = order
        }
        const res = await DashboardService.getEmployees(params)
        setEmployees(res?.data || [])
        setEmployeesMeta(
          res?.meta || {
            current_page: 1, next_page: null, prev_page: null, total_page: 1, total: 0,
          },
        )
      } catch (e) {
        myToaster(e)
      } finally {
        setIsLoading(false)
      }
    },
    [limit],
  )

  // ─── Effects ──────────────────────────────────────────────────────────────
  // Header / metrics / chart refetch when the (validated) company changes.
  useEffect(() => {
    if (!effectiveCompanyId) return
    fetchOverview(effectiveCompanyId)
    fetchChart(effectiveCompanyId)
    fetchMetrics(effectiveCompanyId, activeKey(metrics))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCompanyId])

  // Debounced search → resets to page 1 and refetches the table.
  const searchTimer = useRef(null)
  useEffect(() => {
    if (!effectiveCompanyId) return undefined
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      setPage(1)
      fetchEmployees(effectiveCompanyId, {
        search: searchTerm,
        kol: kolParamFromActiveKey(activeKey(metrics)),
        sort: sortField,
        order: sortOrder,
        pageArg: 1,
      })
    }, 300)
    return () => searchTimer.current && clearTimeout(searchTimer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, effectiveCompanyId])

  // Table fetch on page / sort / metric-card / company change.
  useEffect(() => {
    if (!effectiveCompanyId) return
    fetchEmployees(effectiveCompanyId, {
      search: searchTerm,
      kol: kolParamFromActiveKey(activeKey(metrics)),
      sort: sortField,
      order: sortOrder,
      pageArg: page,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCompanyId, page, sortField, sortOrder, metrics])

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleMetricClick = useCallback((key) => {
    setMetrics((prev) => prev.map((m) => ({ ...m, active: m.key === key })))
    setPage(1)
  }, [])

  const handleSort = useCallback(({ sort, order }) => {
    setSortField(sort || null)
    setSortOrder(order || null)
    setPage(1)
  }, [])

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handleSelectionChange = useCallback((updated) => {
    setEmployees(updated.data || [])
  }, [])

  // ─── Context value ────────────────────────────────────────────────────────
  const contextValue = useMemo(
    () => ({
      // company
      companies,
      companyId,
      activeCompany,
      setCompanyId,
      // header
      overview,
      // metrics
      metrics,
      handleMetricClick,
      // charts
      employeeSeries,
      candidateSeries,
      chartLabels,
      // table
      employees,
      employeesMeta,
      page,
      limit,
      isLoading,
      searchTerm,
      setSearchTerm,
      handleSort,
      handlePageChange,
      handleSelectionChange,
      sortField,
      sortOrder,
    }),
    [
      companies, companyId, activeCompany, setCompanyId,
      overview, metrics, handleMetricClick,
      employeeSeries, candidateSeries, chartLabels,
      employees, employeesMeta, page, limit, isLoading,
      searchTerm, handleSort, handlePageChange, handleSelectionChange,
      sortField, sortOrder,
    ],
  )

  return (
    <DashboardContext.Provider value={contextValue}>{children}</DashboardContext.Provider>
  )
}

const useDashboard = () => {
  const context = useContext(DashboardContext)
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider')
  }
  return context
}

export { DashboardProvider, useDashboard }
